const LINE_LOGIN_CALLBACK_KEY = 'lineLoginCallback';
const SHARED_CALLBACK_PREFIX = `${LINE_LOGIN_CALLBACK_KEY}:`;
const CALLBACK_TTL = 15 * 60 * 1000;

type StorageName = 'localStorage' | 'sessionStorage';
type Callback = { path: string; state: string; expiresAt: number; shared: boolean };

const normalizeCallback = (cb: unknown): string | null => {
	if (typeof cb !== 'string' || !cb || typeof window === 'undefined') return null;
	try {
		const url = new URL(cb, window.location.origin);
		if (url.origin !== window.location.origin || url.pathname.startsWith('/business-login')) return null;
		return `${url.pathname}${url.search}${url.hash}`;
	} catch {
		return null;
	}
};

const readCallback = (storage: StorageName, key: string): Callback | null => {
	if (typeof window === 'undefined') return null;
	try {
		const entry = JSON.parse(window[storage].getItem(key) || 'null');
		if (!entry || typeof entry.state !== 'string' || !entry.state.trim() || typeof entry.expiresAt !== 'number' ||
			entry.expiresAt <= Date.now() || typeof entry.shared !== 'boolean') return null;
		const path = normalizeCallback(entry.path);
		return path ? { ...entry, path } : null;
	} catch {
		return null;
	}
};

const removeCallback = (storage: StorageName, key: string): void => {
	if (typeof window === 'undefined') return;
	try {
		window[storage].removeItem(key);
	} catch {
		// 儲存空間被瀏覽器停用時，仍讓登入繼續。
	}
};

const writeCallback = (storage: StorageName, key: string, entry: Callback): boolean => {
	try {
		window[storage].setItem(key, JSON.stringify(entry));
		return true;
	} catch {
		return false;
	}
};

const sharedKey = (state: string): string => `${SHARED_CALLBACK_PREFIX}${encodeURIComponent(state)}`;

export const clearLineLoginCallback = (oauthState?: unknown): void => {
	const pending = readCallback('sessionStorage', LINE_LOGIN_CALLBACK_KEY);
	const state = oauthState === undefined ? pending?.state : oauthState;
	if (typeof state === 'string' && state) removeCallback('localStorage', sharedKey(state));
	if (oauthState === undefined || pending?.state === state) {
		removeCallback('sessionStorage', LINE_LOGIN_CALLBACK_KEY);
	}
};

export const saveLineLoginCallback = (cb: unknown, authorizationUrl: string): void => {
	clearLineLoginCallback();
	const path = normalizeCallback(cb);
	if (!path) return;
	let state = '';
	try {
		state = new URL(authorizationUrl, window.location.origin).searchParams.get('state') || '';
	} catch {
		return;
	}
	// 不保存缺少有效 OAuth state 的入口；保留原值，避免改變後端產生的識別碼。
	if (!state.trim()) return;
	const entry: Callback = { path, state, expiresAt: Date.now() + CALLBACK_TTL, shared: false };
	// Safari 從 LINE App 返回可能另開分頁；以既有 OAuth state 對應入口，避免不同登入互相覆蓋。
	entry.shared = writeCallback('localStorage', sharedKey(state), { ...entry, shared: true });
	writeCallback('sessionStorage', LINE_LOGIN_CALLBACK_KEY, entry);
	// 清理已過期或損壞的入口；取消後未返回的登入也會過期。
	try {
		Object.keys(window.localStorage).forEach(key => {
			if (key.startsWith(SHARED_CALLBACK_PREFIX) && !readCallback('localStorage', key)) {
				removeCallback('localStorage', key);
			}
		});
	} catch {
		// localStorage 不可用時，仍保留 sessionStorage 的同分頁支援。
	}
};

export const takeLineLoginCallback = (oauthState?: unknown): string | null => {
	const pending = readCallback('sessionStorage', LINE_LOGIN_CALLBACK_KEY);
	const state = oauthState === undefined ? pending?.state : oauthState;
	if (typeof state !== 'string' || !state.trim()) return null;
	const shared = readCallback('localStorage', sharedKey(state));
	// 已在另一分頁消耗的 shared callback 不可從原分頁的副本重用。
	const entry = shared || (pending?.state === state && !pending.shared ? pending : null);
	clearLineLoginCallback(oauthState);
	return entry?.state === state ? entry.path : null;
};
